import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const SequenceGrammarPoints = () => {
  const title = 'Sequence Grammar Points';
  const image = '/Images/grammar1.webp';
  const intro = 'Teaching grammar in a logical order prevents cognitive overload and builds on what learners already know. This guide maps a principled sequence from A1 to C1, with rationale, prerequisite checks, and common transfer errors.';

  const seoContent = [
    'Grammar sequencing isn\'t one-size-fits-all, but research and classroom experience converge on a few principles: teach high-frequency, high-communicative-value structures first; group by meaning/function rather than form alone; ensure prerequisites are solid before adding complexity; and recycle earlier points in new contexts.',
    'A proven A1–A2 spine: be/have → present simple → present continuous → past simple (regular) → past simple (irregular high-frequency) → future with going to → present perfect (experience) → comparatives/superlatives → modals (can, have to, should). Each step adds one new form while reusing known vocabulary.',
    'At B1–B2, expand the perfect aspect (present perfect continuous, past perfect), introduce passive voice, conditionals (zero → first → second → third), reported speech, and relative clauses. Sequence these by semantic progression: time relations → hypothetical meaning → information packaging.',
    'C1–C2 adds: advanced passive (get-passive, causative), subjunctive/conditionals mixed, inversion, ellipsis/substitution, and discourse markers. Here the focus shifts from form to register, nuance, and written vs. spoken distribution.',
    'Always run a quick prerequisite check before a new point: can they form the auxiliary? Do they know the participle? Can they distinguish the time reference? A 2-minute diagnostic saves 20 minutes of confusion later.',
  ];

  const levels = [
    {
      level: 'a1-a2',
      label: 'A1–A2 Core Sequence',
      tagline: 'Foundation structures',
      cards: [
        {
          title: 'Be & Have Got',
          image: '/Images/grammar1.webp',
          desc: 'Forms, short answers, negatives, and the have/have got distinction with concept checks.',
          links: [{ text: 'View Module', to: '/grammar-exercises/be-have' }],
        },
        {
          title: 'Present Simple vs. Continuous',
          image: '/Images/2people1.webp',
          desc: 'State vs. action verbs, time markers, and the \"now vs. around now\" timeline.',
          links: [{ text: 'View Module', to: '/grammar-exercises/present-simple-continuous' }],
        },
        {
          title: 'Past Simple: Regular & Irregular',
          image: '/Images/vocabulary.webp',
          desc: '-ed rules, top 50 irregulars grouped by pattern, and pronunciation of -ed endings.',
          links: [{ text: 'View Module', to: '/grammar-exercises/past-simple' }],
        },
        {
          title: 'Future: Going To & Will',
          image: '/Images/online-teacher.webp',
          desc: 'Plan vs. prediction vs. spontaneous decision with decision-tree flowchart.',
          links: [{ text: 'View Module', to: '/grammar-exercises/future' }],
        },
      ],
    },
    {
      level: 'b1-b2',
      label: 'B1–B2 Expansion',
      tagline: 'Complexity & nuance',
      cards: [
        {
          title: 'Perfect Aspect Family',
          image: '/Images/tutor2-1280x470.webp',
          desc: 'Present perfect continuous, past perfect, future perfect—timeline comparison chart.',
          links: [{ text: 'View Module', to: '/grammar-exercises/perfect-aspect' }],
        },
        {
          title: 'Conditionals 0–3 + Mixed',
          image: '/Images/3people1a.webp',
          desc: 'Form tables, meaning continua, and the \"if I were you\" subjunctive note.',
          links: [{ text: 'View Module', to: '/grammar-exercises/conditionals' }],
        },
        {
          title: 'Passive Voice Progression',
          image: '/Images/quiz1.webp',
          desc: 'Present/past passive → modal passive → get-passive → causative (have/get done).',
          links: [{ text: 'View Module', to: '/grammar-exercises/passive' }],
        },
      ],
    },
    {
      level: 'c1-c2',
      label: 'C1–C2 Refinement',
      tagline: 'Register & discourse',
      cards: [
        {
          title: 'Advanced Conditionals & Inversion',
          image: '/Images/grammar1.webp',
          desc: 'Mixed timeframes, \"had I known,\" \"should you need,\' and rhetorical conditionals.',
          links: [{ text: 'View Module', to: '/grammar-exercises/advanced-conditionals' }],
        },
        {
          title: 'Ellipsis, Substitution & Cohesion',
          image: '/Images/2people1.webp',
          desc: 'So/neither do I, do so, the one/ones, and textual reference chains.',
          links: [{ text: 'View Module', to: '/grammar-exercises/ellipsis' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Grammar Sequencing Guide: A1–C2 Order with Prerequisites & Transfer Errors"
      seoDescription="Principled grammar teaching order from beginner to advanced. Includes prerequisite checks, timeline diagrams, concept-check questions, and common L1 transfer errors for each structure."
    />
  );
};

export default SequenceGrammarPoints;